import type { ReactNode } from 'react';
import type { Tone } from '../lib/format.js';

/** Uma coluna da tabela: como titular e como renderizar a célula. */
export interface Column<T> {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  /** Números/dinheiro/IDs: alinha à direita e usa monoespaçado tabular. */
  num?: boolean;
  align?: 'left' | 'right';
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  keyOf: (row: T) => string;
  onRowClick?: (row: T) => void;
  /** Aresta esquerda semântica por linha (varredura por estado). */
  rowTone?: (row: T) => Tone;
}

/**
 * Tabela genérica do painel.
 *
 * Rola no próprio contêiner (`table-wrap`) para nunca empurrar o corpo da página
 * na horizontal, e o cabeçalho gruda no topo durante a rolagem vertical do card.
 */
export function DataTable<T>({ columns, rows, keyOf, onRowClick, rowTone }: DataTableProps<T>) {
  return (
    <div className="table-wrap">
      <table className={`table${onRowClick ? ' table--clickable' : ''}`}>
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key} className={col.num || col.align === 'right' ? 'td--right' : undefined}>
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={keyOf(row)}
              data-tone={rowTone ? rowTone(row) : undefined}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              tabIndex={onRowClick ? 0 : undefined}
              onKeyDown={
                onRowClick
                  ? (e) => {
                      if (e.key === 'Enter') onRowClick(row);
                    }
                  : undefined
              }
            >
              {columns.map((col) => (
                <td key={col.key} className={col.num ? 'td--num' : col.align === 'right' ? 'td--right' : undefined}>
                  {col.cell(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
